import { useContext, useEffect, useMemo, useState } from "react";
import {
  Box, Button, Card, CardContent, Chip, Divider, Input, LinearProgress,
  Sheet, Stack, Tooltip, Typography,
} from "@mui/joy";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import HourglassEmptyRoundedIcon from "@mui/icons-material/HourglassEmptyRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import Cookies from "js-cookie";

import { LayoutContext } from "../context";
import { APIMyPayments, APIMyPaymentBreakdown } from "../api";
import PaymentBreakdown, { fmtMoney } from "../components/Payments/PaymentBreakdown";

const ROLE_LABEL = { surveyor: "Обмер", draftsman: "Черчение" };
const ROLE_COLOR = { surveyor: "#6d28d9", draftsman: "#0891b2" };

function PaymentRow({ item, token }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !rows) {
      setLoading(true);
      const d = await APIMyPaymentBreakdown(item.payment_id, token);
      if (d?.error) setError(d.error);
      else setRows(d.rows || []);
      setLoading(false);
    }
  };

  const accent = ROLE_COLOR[item.role] || "#6d28d9";

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: "16px",
        borderColor: "transparent",
        boxShadow: "0 8px 24px -18px rgba(0,0,0,0.35)",
        mb: 1.25,
      }}
    >
      <CardContent sx={{ p: 1.75 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: "wrap", mb: 0.5 }}>
              <Chip
                size="sm" variant="soft"
                sx={{ bgcolor: `${accent}14`, color: accent, fontWeight: 600 }}
              >
                {ROLE_LABEL[item.role] || item.role}
              </Chip>
              {item.deal_id && (
                <Chip size="sm" variant="soft" color="neutral">№{item.deal_id}</Chip>
              )}
              {item.payment_status === 'paid' || (item.payment_status == null && item.is_paid) ? (
                <Chip size="sm" variant="soft" color="success" startDecorator={<PaidRoundedIcon sx={{ fontSize: 14 }} />}>
                  выплачено
                </Chip>
              ) : item.payment_status === 'no_konsol_task' ? (
                <Tooltip
                  size="sm" variant="outlined"
                  title="Нет задания в Консоли — статус выплаты пока не подтверждён"
                >
                  <Chip size="sm" variant="soft" color="neutral" startDecorator={<HelpOutlineRoundedIcon sx={{ fontSize: 14 }} />}>
                    нет задания в Консоли
                  </Chip>
                </Tooltip>
              ) : (
                <Chip size="sm" variant="soft" color="warning" startDecorator={<HourglassEmptyRoundedIcon sx={{ fontSize: 14 }} />}>
                  {item.payment_status === 'processing' ? 'оформляется' : 'ожидает выплаты'}
                </Chip>
              )}
            </Stack>

            {item.address && (
              <Stack direction="row" spacing={0.5} alignItems="flex-start">
                <PlaceRoundedIcon sx={{ fontSize: 16, color: "text.tertiary", mt: 0.2 }} />
                <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                  {item.address}
                </Typography>
              </Stack>
            )}

            <Typography level="body-xs" sx={{ color: "text.tertiary", mt: 0.25 }}>
              {item.date_event
                ? `Событие ${new Date(item.date_event).toLocaleDateString("ru-RU")}`
                : "Дата события не указана"}
              {item.paid_date
                ? ` · выплачено ${new Date(item.paid_date).toLocaleDateString("ru-RU")}`
                : ""}
            </Typography>
          </Box>

          <Stack alignItems={{ xs: "flex-start", sm: "flex-end" }} spacing={0.5}>
            <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: accent, lineHeight: 1 }}>
              {fmtMoney(item.total)} ₽
            </Typography>
            <Button
              size="sm" variant="plain" color="primary"
              startDecorator={<ReceiptLongRoundedIcon fontSize="small" />}
              onClick={toggle}
              sx={{ minHeight: 26, px: 0.75 }}
            >
              {open ? "Скрыть расчёт" : "Из чего сумма"}
            </Button>
          </Stack>
        </Stack>

        {open && (
          <Box sx={{ mt: 1.25 }}>
            {loading && <LinearProgress sx={{ my: 1 }} />}
            {error && (
              <Typography level="body-sm" color="danger">{error}</Typography>
            )}
            {rows && <PaymentBreakdown compact rows={rows} accent={accent} />}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default function MyPaymentsPage() {
  const { tokenEmployee } = useContext(LayoutContext);
  const token = Cookies.get("auth_token") || tokenEmployee;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filter, setFilter] = useState("all"); // all | unpaid | paid
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  // Агрегаты и градация приходят с сервера (по ВСЕЙ выборке, не по странице)
  const [serverTotals, setServerTotals] = useState({ accrued: 0, paid: 0, pending: 0, count: 0, paid_count: 0, unpaid_count: 0 });
  const [grade, setGrade] = useState(null);
  // Фильтр по датам (по умолчанию — за всё время)
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Загрузка первой страницы (и при смене фильтра дат)
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setPage(1);
      const d = await APIMyPayments(token, { page: 1, pageSize: 50, dateFrom: dateFrom || null, dateTo: dateTo || null });
      if (!alive) return;
      setItems(d?.results || []);
      setServerTotals(d?.totals || { accrued: 0, paid: 0, pending: 0, count: 0, paid_count: 0, unpaid_count: 0 });
      setGrade(d?.grade || null);
      setHasMore(!!d?.has_more);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [token, dateFrom, dateTo]);

  const loadMore = async () => {
    const next = page + 1;
    setLoadingMore(true);
    const d = await APIMyPayments(token, { page: next, pageSize: 50, dateFrom: dateFrom || null, dateTo: dateTo || null });
    setItems((prev) => [...prev, ...(d?.results || [])]);
    setHasMore(!!d?.has_more);
    setPage(next);
    setLoadingMore(false);
  };

  // Суммы — с сервера (полные, не по обрезанному списку)
  const totals = { accrued: serverTotals.accrued, paid: serverTotals.paid, pending: serverTotals.pending };

  const visible = useMemo(() => {
    if (filter === "unpaid") return items.filter((i) => !i.is_paid);
    if (filter === "paid") return items.filter((i) => i.is_paid);
    return items;
  }, [items, filter]);

  return (
    <Box sx={{ px: { xs: 2, md: 6 }, mt: { xs: 2, md: 3 }, pb: 4 }}>
      <Typography level="h3" sx={{ mb: 0.5 }}>Мои выплаты</Typography>
      <Typography level="body-sm" sx={{ color: "text.tertiary", mb: 2 }}>
        Суммы приходят из системы расчёта. Нажмите «Из чего сумма», чтобы увидеть,
        как она сложилась.
      </Typography>

      {/* ── Сводка ── */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} sx={{ mb: 2 }}>
        <Sheet variant="soft" sx={{ flex: 1, p: 1.75, borderRadius: "16px", background: "linear-gradient(135deg,#f3efff,#ede7ff)" }}>
          <Typography level="body-xs" sx={{ color: "#4c1d95", fontWeight: 600 }}>Начислено всего</Typography>
          <Typography sx={{ fontSize: "1.6rem", fontWeight: 800, color: "#4c1d95", lineHeight: 1.2 }}>
            {fmtMoney(totals.accrued)} ₽
          </Typography>
        </Sheet>
        <Sheet variant="soft" sx={{ flex: 1, p: 1.75, borderRadius: "16px", background: "linear-gradient(135deg,#ecfdf5,#d1fae5)" }}>
          <Typography level="body-xs" sx={{ color: "#065f46", fontWeight: 600 }}>Выплачено</Typography>
          <Typography sx={{ fontSize: "1.6rem", fontWeight: 800, color: "#065f46", lineHeight: 1.2 }}>
            {fmtMoney(totals.paid)} ₽
          </Typography>
        </Sheet>
        <Sheet variant="soft" sx={{ flex: 1, p: 1.75, borderRadius: "16px", background: "linear-gradient(135deg,#fffbeb,#fef3c7)" }}>
          <Typography level="body-xs" sx={{ color: "#92400e", fontWeight: 600 }}>Ожидает выплаты</Typography>
          <Typography sx={{ fontSize: "1.6rem", fontWeight: 800, color: "#92400e", lineHeight: 1.2 }}>
            {fmtMoney(totals.pending)} ₽
          </Typography>
        </Sheet>
      </Stack>

      {grade && (
        <Sheet variant="soft" sx={{ mb: 2, p: 1.5, borderRadius: "14px", background: "linear-gradient(135deg,#eef2ff,#e0e7ff)" }}>
          <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ gap: 1.5 }}>
            <Chip color="primary" variant="solid" sx={{ fontWeight: 700, textTransform: "capitalize" }}>
              {grade.level}
            </Chip>
            <Typography level="body-sm">
              Минималка за работу: <b>{fmtMoney(grade.minimum)} ₽</b>
            </Typography>
            <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
              Выполнено работ: {grade.completed_count}
              {grade.next_level && grade.to_next != null && (
                <> · до уровня «{grade.next_level}» ещё {grade.to_next}</>
              )}
            </Typography>
          </Stack>
        </Sheet>
      )}

      {/* ── Фильтр ── */}
      <Stack direction={"row"} spacing={1} sx={{ mb: 1.5 }} alignItems={{ sm: "center" }}>
        <Typography level="body-xs" sx={{ color: "text.tertiary" }}>Период:</Typography>
        <Input type="date" size="sm" sx={{ width: 'fit-content' }} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        <Typography level="body-xs">—</Typography>
        <Input type="date" size="sm" sx={{ width: 'fit-content' }} value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        {(dateFrom || dateTo) && (
          <Chip size="sm" variant="soft" color="neutral" onClick={() => { setDateFrom(""); setDateTo(""); }} sx={{ cursor: "pointer" }}>
            Сбросить
          </Chip>
        )}
      </Stack>

      <Stack direction="row" spacing={0.75} sx={{ mb: 1.5, flexWrap: "wrap" }}>
        {[
          { v: "all", l: `Все (${serverTotals.count})` },
          { v: "unpaid", l: `Ожидают (${serverTotals.unpaid_count})` },
          { v: "paid", l: `Выплачены (${serverTotals.paid_count})` },
        ].map((f) => (
          <Chip
            key={f.v}
            variant={filter === f.v ? "solid" : "outlined"}
            color={filter === f.v ? "primary" : "neutral"}
            onClick={() => setFilter(f.v)}
            sx={{ cursor: "pointer", borderRadius: "10px" }}
          >
            {f.l}
          </Chip>
        ))}
      </Stack>

      <Divider sx={{ mb: 1.5 }} />

      {loading && <LinearProgress />}

      {!loading && visible.length === 0 && (
        <Sheet variant="soft" sx={{ p: 3, borderRadius: "16px", textAlign: "center" }}>
          <Typography level="body-md" sx={{ color: "text.tertiary" }}>
            Выплат пока нет
          </Typography>
        </Sheet>
      )}

      {visible.map((item) => (
        <PaymentRow key={item.payment_id} item={item} token={token} />
      ))}

      {hasMore && (
        <Button
          fullWidth variant="soft" color="neutral" sx={{ mt: 1.5, borderRadius: "12px" }}
          loading={loadingMore} onClick={loadMore}
        >
          Показать ещё
        </Button>
      )}
    </Box>
  );
}